import React, { useState } from 'react';
import TelnyxLogo from '@svg/telnyx';
import styled from 'styled-components';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import RobotIcon from '@svg/robot_icon';

import { useTheme } from '../../styles/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State, authActions, flowActions } from '@store/index';
import { Tooltip } from '@common/Tooltip';
import { logout } from '@store/async';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, alpha } from '@mui/material';
import { ChatWidget } from '@telnyx-private/ai-chat-widget';

import {
  AutoAwesomeMotionOutlined,
  Brightness6Outlined,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  ExitToAppOutlined,
  KeyboardArrowRightOutlined,
  LogoutOutlined,
  SignalCellularAltOutlined,
} from '@mui/icons-material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  bottom: 0;
  left: 0;
  position: fixed;
  top: 0;
  width: 59px;
  height: 100vh;
  z-index: 1000;

  background-color: ${({ theme }) => theme.background.standard};
  border-right: 1px solid ${({ theme }) => theme.line.light};
  box-shadow: 0 0 0 transparent;

  svg {
    font-size: 2.1ch;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 18px 0;

  &:hover {
    cursor: pointer;
  }
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding-top: 8px;
  gap: 6px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 14px;
`;

const UserIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: calc(100% - 8px);
  padding: 0 4px;
  position: relative;

  border-radius: 6px;
  background-color: ${({ theme }) => theme.background.standard};
  color: ${({ theme }) => theme.text.standard};
  border: 1px solid ${({ theme }) => theme.line.light};
  margin: 16px 0;

  & .MuiSvgIcon-root {
    height: 15px;
    width: 15px;
    color: ${({ theme }) => theme.text.subtext};
  }

  & .MuiListItemIcon-root {
    min-width: 24px;
  }

  #arrow {
    position: absolute;
    right: 0;
    left: 28px;
  }

  p {
    font-family: 'PPFormula';
    font-size: 12px;
    margin: 0;
    padding-top: 2px;
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => alpha(theme.background.hover, 0.04)};
  }
`;

const UsernameListItem = styled.div`
  padding: 8px 16px;
  font-weight: bold;
  font-size: 14px;
  font-family: 'PPFormula';
  padding-top: 10px;
`;

const ChatWidgetContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LeftNavbar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === '/';
  const isDocumentation = pathname.startsWith('/documentation');

  const tabs = useSelector((state: State) => state.tabs.list);
  const themeType = useSelector((state: State) => state.flow.theme);
  const name = useSelector((state: State) => state.auth.name);
  const traceRunning = useSelector((state: State) => state.tabs.active.run.running);
  const traceHasId = Boolean(useSelector((state: State) => state.tabs.active.run.id));
  const unsavedWorkflows = useSelector((state: State) => state.tabs.list).filter((workflow) => !workflow.saved);
  const isTelnyxUser = useSelector((state: State) => state.auth.isTelnyxUser);
  const telnyxFeatures = useSelector((state: State) => state.auth.telnyxFeatures);
  const internalRequests = useSelector((state: State) => state.auth.internalRequests);
  const userId = useSelector((state: State) => state.auth.user_id);

  const flowBaseUrl = LOCAL
    ? 'http://localhost:3001/execute/live/assistants/bfa5c802-f758-42ec-8689-704a54c141bb'
    : 'https://api.telnyx.com/v2/flow/execute/live/public_chatbots/3ed4fd78-2903-491d-9374-8ccdcf5459ae';

  const feedbackUrl = LOCAL ? 'http://localhost:3000/datastore/feedback' : 'https://api.telnyx.com/ai/chatbot/feedback';

  const chatWidgetUser = {
    id: userId,
    token: null,
    apiAccessKey: null,
    api_v2_token: null,
    twoFactorAuth: null,
    role: null,
    expiresAt: null,
  };

  const toggleTelnyxOnlyFeatures = () => {
    dispatch(authActions.toggleTelnyxFeatures());
  };

  const toggleTheme = () => {
    if (themeType === 'light') {
      dispatch(flowActions.setTheme('dark'));
    }

    if (themeType === 'dark') {
      dispatch(flowActions.setTheme('light'));
    }
  };

  const navigateToHome = () => {
    if (tabs.some((tab) => !tab.saved)) return;
    navigate('/');
  };

  const navigateToExternalLink = (url: string) => {
    if (unsavedWorkflows.length > 0 && !location.pathname.includes('documentation')) {
      dispatch(flowActions.setShowUnsavedType('link'));
      dispatch(flowActions.setShowUnsaved(url));
    } else {
      window.location.href = url;
    }
  };

  const nameInitials =
    name
      ?.split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('') || 'U';

  const navigateToLink = (path: string) => {
    if (unsavedWorkflows.length > 0 && !location.pathname.includes('documentation'))
      return dispatch(flowActions.setShowUnsaved(path));
    navigate(path);
  };

  return (
    <Container>
      <div>
        <Header onClick={navigateToHome}>
          <TelnyxLogo height={23} title='Telnyx' style={{ color: theme.text.standard }} />
        </Header>
        <MenuContainer>
          <Tooltip title='Workspaces' placement='right' arrow>
            <IconButton
              size='small'
              sx={{ backgroundColor: isHome ? `${alpha(theme.primary, 0.12)} !important` : 'transparent' }}
              onClick={() => navigateToLink('/')}
              data-intercom-target='workspaces-button'
            >
              <AutoAwesomeMotionOutlined sx={{ color: isHome ? theme.primary : theme.text.standard }} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Documentation' placement='right' arrow>
            <IconButton
              size='small'
              sx={{
                backgroundColor: isDocumentation ? `${alpha(theme.primary, 0.12)} !important` : 'transparent',
                fontSize: '1.6ch !important',
              }}
              onClick={() => navigateToLink(`/documentation/getting_started`)}
            >
              <ArticleOutlinedIcon sx={{ color: isDocumentation ? theme.primary : theme.text.standard }} />
            </IconButton>
          </Tooltip>
        </MenuContainer>
      </div>
      <Footer>
        <Stack spacing={1}>
          <ChatWidgetContainer>
            <Tooltip title='AI Chat' placement='right' arrow>
              <IconButton
                size='small'
                sx={{
                  backgroundColor: open ? `${alpha(theme.primary, 0.12)} !important` : 'transparent',
                }}
                onClick={() => setOpen((prev) => !prev)}
              >
                <RobotIcon
                  height={18.85}
                  width={18.85}
                  color={open ? theme.primary : theme.text.standard}
                  primary_color={open ? theme.primary : theme.text.standard}
                />
              </IconButton>
            </Tooltip>
            <ChatWidget
              flowBaseUrl={flowBaseUrl}
              feedbackUrl={feedbackUrl}
              user={chatWidgetUser}
              open={open}
              setOpen={setOpen}
              mountPosition='top-right'
              mountDirection='outside'
              offsetX={25}
              offsetY={24}
              placeholders={[
                'How do I create a voice workflow?',
                'What is Flow?',
                'How do I enable streaming?',
                'How do I run my workflow?',
                'What are data prefixes?',
              ]}
            />
          </ChatWidgetContainer>
          <Tooltip title='Change Theme' placement='right' arrow>
            <IconButton size='small' onClick={toggleTheme} disabled={traceRunning || traceHasId}>
              <Brightness6Outlined
                sx={{
                  color: traceRunning || traceHasId ? alpha(theme.text.standard, 0.16) : theme.text.standard,
                }}
              />
            </IconButton>
          </Tooltip>
          <Divider orientation='horizontal' flexItem />
        </Stack>
        <UserIcon onClick={(e) => setAnchorEl(e.currentTarget)}>
          <p>{nameInitials}</p>
          <div id='arrow'>
            <KeyboardArrowRightOutlined />
          </div>
        </UserIcon>
        <Menu
          id='user-menu'
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          sx={{ minWidth: '300px' }}
          transformOrigin={{
            vertical: 'top',
            horizontal: -24,
          }}
          slotProps={{
            paper: { sx: { minWidth: '200px', border: `1px solid ${theme.line.light}` } },
          }}
          MenuListProps={{
            'aria-labelledby': 'user-menu',
          }}
        >
          <UsernameListItem>{name}</UsernameListItem>
          <Divider sx={{ marginBottom: '8px' }} />
          {isTelnyxUser && (
            <Stack>
              <MenuItem onClick={toggleTelnyxOnlyFeatures}>
                <ListItemIcon>
                  {telnyxFeatures ? (
                    <CheckBoxOutlined fontSize='small' color='primary' />
                  ) : (
                    <CheckBoxOutlineBlankOutlined fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>Telnyx Features</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => dispatch(authActions.toggleInternalRequests())}>
                <ListItemIcon>
                  {internalRequests ? (
                    <CheckBoxOutlined fontSize='small' color='primary' />
                  ) : (
                    <CheckBoxOutlineBlankOutlined fontSize='small' />
                  )}
                </ListItemIcon>
                <ListItemText>Internal Requests</ListItemText>
              </MenuItem>
              <Divider />
            </Stack>
          )}
          <MenuItem
            onClick={() =>
              navigateToExternalLink(
                window.location.origin.includes('dev') ? 'https://portaldev.telnyx.com' : 'https://portal.telnyx.com'
              )
            }
          >
            <ListItemIcon>
              <ExitToAppOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText>Portal</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => navigateToExternalLink('https://developers.telnyx.com/')}>
            <ListItemIcon>
              <ExitToAppOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText>API Docs</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => navigateToExternalLink('https://status.telnyx.com/')}>
            <ListItemIcon>
              <SignalCellularAltOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText>Platform Status</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => dispatch(logout())} sx={{ marginBottom: '7px' }}>
            <ListItemIcon>
              <LogoutOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Footer>
    </Container>
  );
};
